/**
 * WeatherDetail + WeatherHistoryCard + formatWindowTime
 * Extracted from DetailPage.jsx (A12 Sprint 5).
 *
 * Temperature/precipitation charts, spray window timeline, fieldwork
 * traffic lights, daily breakdown, and lazy-loaded history comparison.
 *
 * Co-extracted private helpers:
 *   - WeatherHistoryCard (lazy-loaded history comparison card)
 *   - formatWindowTime (ISO → "d.M. HH:00" formatter)
 *
 * A6-26 fix: raw fetch → apiClient.get (WeatherHistoryCard line 350)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import EmptyState from '../../components/EmptyState';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Area, Bar,
  ReferenceLine,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// ─── formatWindowTime (private utility) ──────────────────────
function formatWindowTime(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return `${d.getDate()}.${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, '0')}:00`;
  } catch { return isoStr.slice(5, 16); }
}

// ─── WeatherHistoryCard (private helper) ─────────────────────
function WeatherHistoryCard({ token, t }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    if (!token || loading || loaded) return;
    setLoading(true);
    // A6-26 fix: raw fetch → apiClient.get
    apiClient.get('/api/v1/aif/weather/history-comparison')
      .then(d => { setHistory(d); setLoaded(true); })
      .catch(() => setLoaded(true))
      .finally(() => setLoading(false));
  };

  if (!token) return null;

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--aif-text-secondary)]">📅 {t('weather.historyComparison')}</span>
        {!loaded && (
          <button onClick={load} disabled={loading}
            className="text-xs text-[var(--aif-color-primary)] disabled:opacity-50">
            {loading ? '…' : t('ui.load')}
          </button>
        )}
      </div>
      {history && (
        <div className="mt-2 text-xs text-[var(--aif-text-muted)]">
          <p className="text-[var(--aif-text-primary)]">{history.summary}</p>
          {history.current_week && (
            <div className="flex gap-4 mt-1.5">
              <span>{t('weather.thisWeek')}: {history.current_week.avg_max_c}°C max, {history.current_week.gdd_7d} GDD</span>
              <span>{t('weather.lastYear')}: {history.last_year.avg_max_c}°C max, {history.last_year.gdd_7d} GDD</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── WeatherDetail (main export) ─────────────────────────────
export default function WeatherDetail({ data, sprayWindows, fieldwork, token }) {
  const { t } = useTranslation();
  if (!data?.daily?.length) {
    return (
      <EmptyState
        icon="weather"
        title={t('weather.emptyTitle')}
        subtitle={t('weather.emptySubtitle')}
        testId="weather-empty-state"
      />
    );
  }

  const chartData = data.daily.map(d => ({
    name: d.day_name?.slice(0, 2) || d.date?.slice(5),
    date: d.date,
    tempMax: d.temp_max_c,
    tempMin: d.temp_min_c,
    precip: d.precipitation_total_mm || 0,
    wind: d.wind_avg_ms || 0,
    windMax: d.wind_max_ms || 0,
  }));

  // Compute spray suitability per day from daily data
  const sprayByDay = data.daily.map(d => {
    const wind = d.wind_avg_ms || 0;
    const tempMax = d.temp_max_c || 0;
    const tempMin = d.temp_min_c || 0;
    const precip = d.precipitation_total_mm || 0;
    const ok = wind < 5 && tempMin >= 5 && tempMax <= 25 && precip < 0.5;
    return { date: d.date, dayName: d.day_name?.slice(0, 2), ok };
  });

  // Fieldwork suitability per day
  const fieldworkDays = fieldwork?.days || [];

  return (
    <div className="space-y-6">
      <div className="text-xs text-[var(--aif-text-muted)]">📍 {data.station_name}</div>

      {/* ── Temperature + Precipitation composed chart ── */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('dashboard.tempPrecip')}
        </h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis yAxisId="temp" tick={AXIS_TICK} unit="°" />
              <YAxis yAxisId="precip" orientation="right" tick={AXIS_TICK} unit="mm" />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Area yAxisId="temp" type="monotone" dataKey="tempMax" stroke="#ef4444" fill="none" strokeWidth={2} name="Max °C" />
              <Area yAxisId="temp" type="monotone" dataKey="tempMin" stroke="#3b82f6" fill="none" strokeWidth={2} name="Min °C" />
              {/* Fill between min and max */}
              <Area yAxisId="temp" type="monotone" dataKey="tempMax" stroke="none" fill="#93c5fd" fillOpacity={0.15} name="" legendType="none" />
              <Area yAxisId="temp" type="monotone" dataKey="tempMin" stroke="none" fill="var(--aif-bg-page)" fillOpacity={1} name="" legendType="none" />
              <Bar yAxisId="precip" dataKey="precip" fill="#60a5fa" opacity={0.6} name={t('detail.precipMm')} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Wind chart with threshold ── */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('detail.wind')}
        </h3>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} unit=" m/s" domain={[0, 'auto']} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <ReferenceLine y={5} stroke="#ef4444" strokeDasharray="6 3" label={{ value: t('detail.sprayThreshold'), position: 'right', fill: '#ef4444', fontSize: 10 }} />
              <Line type="monotone" dataKey="wind" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} name={t('detail.windAvg')} />
              <Line type="monotone" dataKey="windMax" stroke="#c084fc" strokeWidth={1} strokeDasharray="4 2" dot={false} name={t('detail.windMax')} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Spray Window Timeline ── */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('detail.sprayWindows')}
        </h3>
        <div className="flex gap-1.5">
          {sprayByDay.map((d, i) => (
            <div key={i} className="flex-1 text-center">
              <div
                className={`h-8 rounded-lg mb-1.5 flex items-center justify-center text-xs font-medium transition-all ${
                  d.ok
                    ? 'bg-green-500/20 border border-green-500/40 text-green-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}
              >
                {d.ok ? '✓' : '✕'}
              </div>
              <span className="text-[10px] text-[var(--aif-text-muted)]">{d.dayName}</span>
            </div>
          ))}
        </div>
        {sprayWindows?.windows?.length > 0 && (
          <div className="mt-3 space-y-2">
            {sprayWindows.windows.slice(0, 3).map((w, i) => (
              <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-xl border text-xs ${
                w.quality === 'good' ? 'border-green-500/30 bg-green-500/10' :
                w.quality === 'acceptable' ? 'border-yellow-500/30 bg-yellow-500/10' :
                'border-gray-500/30 bg-gray-500/10'
              }`}>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  w.quality === 'good' ? 'bg-green-500' : w.quality === 'acceptable' ? 'bg-yellow-500' : 'bg-gray-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <span className="text-[var(--aif-text-primary)] font-medium">
                    {formatWindowTime(w.start)} – {formatWindowTime(w.end)}
                  </span>
                  <span className="text-[var(--aif-text-muted)] ml-2">
                    {w.duration_hours}h | {w.avg_wind_ms} m/s | {w.avg_temperature_c}°C
                  </span>
                </div>
                <span className={`px-2 py-0.5 rounded-full font-medium ${
                  w.quality === 'good' ? 'text-green-400' : w.quality === 'acceptable' ? 'text-yellow-400' : 'text-gray-400'
                }`}>
                  {w.quality === 'good' ? t('detail.good') : w.quality === 'acceptable' ? t('detail.acceptable') : t('detail.marginal')}
                </span>
              </div>
            ))}
          </div>
        )}
        {sprayWindows?.note && (
          <p className="text-xs text-[var(--aif-text-muted)] mt-2 italic">{sprayWindows.note}</p>
        )}
      </div>

      {/* ── Fieldwork Suitability Traffic Lights ── */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('detail.fieldwork')}
        </h3>
        <div className="flex gap-1.5">
          {(fieldworkDays.length > 0 ? fieldworkDays : data.daily).map((d, i) => {
            const fwd = fieldworkDays[i];
            const color = fwd
              ? (fwd.suitable ? 'green' : fwd.soil_workability === 'marginal' ? 'yellow' : 'red')
              : (d.precipitation_total_mm < 2 && (d.wind_max_ms || 0) < 8 ? 'green' : d.precipitation_total_mm < 5 ? 'yellow' : 'red');
            const dayLabel = fwd?.day_name?.slice(0, 2) || d.day_name?.slice(0, 2) || d.date?.slice(5);
            return (
              <div key={i} className="flex-1 text-center">
                <div className="flex justify-center mb-1.5">
                  <div className={`w-6 h-6 rounded-full ${
                    color === 'green' ? 'bg-green-500' : color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                </div>
                <span className="text-[10px] text-[var(--aif-text-muted)]">{dayLabel}</span>
              </div>
            );
          })}
        </div>
        {fieldworkDays.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {fieldworkDays.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  d.suitable ? 'bg-green-500' : d.soil_workability === 'marginal' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                <span className="text-[var(--aif-text-primary)] font-medium w-16">{d.day_name?.slice(0, 2)} {d.date?.slice(5)}</span>
                <span className="text-[var(--aif-text-muted)] truncate">{d.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Daily breakdown list with spray status ── */}
      <div className="space-y-0">
        {data.daily.map((d, i) => {
          const sd = sprayByDay[i];
          return (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-[var(--aif-border-default)] last:border-0">
              <div className="flex items-center gap-2">
                <div className="text-center w-6">
                  <span className={`text-xs ${sd?.ok ? 'text-green-400' : 'text-red-400'}`}>{sd?.ok ? '✓' : '✕'}</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[var(--aif-text-primary)]">{d.day_name}</div>
                  <div className="text-[10px] text-[var(--aif-text-muted)]">{d.date?.slice(5)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-[var(--aif-text-primary)]">
                  {d.temp_max_c != null ? `${Math.round(d.temp_max_c)}°` : '--'} / {d.temp_min_c != null ? `${Math.round(d.temp_min_c)}°` : '--'}
                </div>
                <div className="text-xs text-[var(--aif-text-muted)]">
                  {d.precipitation_total_mm > 0 ? `💧${d.precipitation_total_mm}mm` : ''}
                  {d.wind_avg_ms != null ? ` 💨${d.wind_avg_ms}` : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── History comparison (lazy-loaded) ── */}
      <WeatherHistoryCard token={token} t={t} />
    </div>
  );
}
